import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axios from 'axios'
import _ from 'lodash'

import { API } from '../../../../api'
import { placeholderSymbolReplace } from '../../../../utils'
import {
  getGenericAssetClasses,
  rankSecurities,
  SEARCHABLE_SECURITY_TYPES,
} from '../investmentUtils'

/**
 * Searches every security type the search covers, by both ticker and name, and merges the hits.
 *
 * @param {string} query
 * @param {Object} cancelToken axios cancel token shared by every request in this search
 * @returns {Promise<Array>} de-duplicated securities
 */
const searchSecurities = async (query, cancelToken) => {
  const value = encodeURIComponent(placeholderSymbolReplace(query, true))

  const responses = await Promise.all(
    _.flatMap(SEARCHABLE_SECURITY_TYPES, securityType => [
      API.get(`security/bySymbol?symbol=${_.toUpper(value)}&securityType=${securityType}`, {
        cancelToken,
      }),
      API.get(`security/byName?name=${value}&securityType=${securityType}`, { cancelToken }),
    ])
  )

  return _.uniqBy(
    _.flatMap(responses, response => response.data || []),
    'id'
  )
}

/**
 * The security search itself — what was typed, what came back, and which of the three lists the
 * user is looking at. Shared by the desktop pop-up and the mobile search screen, which draw the
 * same three lists in very different chrome.
 *
 * @param {Object} params
 * @param {string} params.accountType account type key from the route, e.g. '401k'
 * @param {Array} params.notInvestedInvestments investments already marked not invested
 * @param {Object} [params.inputRef] ref to hand the search field, when the caller needs to focus it
 * @returns {Object}
 */
const useSecuritySearch = ({ accountType, notInvestedInvestments, inputRef }) => {
  const ownInputRef = useRef(null)
  const searchInputRef = inputRef ?? ownInputRef
  const cancelRef = useRef(null)
  const requestRef = useRef(0)

  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [browsingGenerics, setBrowsingGenerics] = useState(false)

  const genericAssetClasses = useMemo(() => getGenericAssetClasses(accountType), [accountType])

  const notInvestedOptions = notInvestedInvestments.map(({ securities }) => ({
    id: securities.id,
    securityName: securities.securityName,
    ticker: securities.ticker,
  }))

  const runSearch = useCallback(async value => {
    const request = (requestRef.current += 1)

    if (cancelRef.current) {
      cancelRef.current()
      cancelRef.current = null
    }

    if (value.length < 1) {
      setResults([])
      setSearching(false)
      return
    }

    try {
      const source = axios.CancelToken.source()
      cancelRef.current = source.cancel

      const securities = await searchSecurities(value, source.token)

      // A slower earlier search must never overwrite the results of a later one
      if (request === requestRef.current) {
        setResults(rankSecurities(securities, value))
        setSearching(false)
      }
    } catch (err) {
      if (axios.isCancel(err)) {
        return
      }
      console.error(err)
      if (request === requestRef.current) {
        setResults([])
        setSearching(false)
      }
    }
  }, [])

  const debouncedSearch = useMemo(() => _.debounce(runSearch, 300), [runSearch])

  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
      if (cancelRef.current) {
        cancelRef.current()
      }
    }
  }, [debouncedSearch])

  const focusInput = () => searchInputRef.current && searchInputRef.current.focus()

  const handleChange = event => {
    const { value } = event.target

    setQuery(value)
    setBrowsingGenerics(false)
    setSearching(value.length > 0)
    debouncedSearch(value)
  }

  // Drops what was typed, abandons any search in flight, and leaves the cursor where it was
  const resetSearch = () => {
    setQuery('')
    debouncedSearch.cancel()
    runSearch('')
    focusInput()
  }

  const browseGenerics = () => {
    resetSearch()
    setBrowsingGenerics(true)
  }

  const backToSearch = () => {
    resetSearch()
    setBrowsingGenerics(false)
  }

  /** Which of the three lists to draw, if any. */
  const content = browsingGenerics
    ? 'generics'
    : query.length > 0
      ? 'results'
      : notInvestedOptions.length > 0
        ? 'notInvested'
        : null

  return {
    inputRef: searchInputRef,
    query,
    results,
    searching,
    browsingGenerics,
    genericAssetClasses,
    notInvestedOptions,
    content,
    focusInput,
    handleChange,
    resetSearch,
    browseGenerics,
    backToSearch,
  }
}

export default useSecuritySearch
